/**
 * Floor Processing Algorithms
 *
 * Pure algorithmic functions for processing floor/room geometry.
 * The main algorithm expands room polygons outward to find wall centerlines.
 */

import * as THREE from 'three';
import { SkeletonNode, SkeletonEdge, FloorSkeleton, ExpandedVertex, RoomConnectivity, SkeletonProbedHit } from '../../../utils/SkeletonProcessingPipeline';
import { buildWallRegionFromRooms, WallRegionComponent } from '../../../utils/WallRegionBuilder';

export const OUTER_ROOM_ID_PREFIX = '__OUTER__';
export const CAVITY_ROOM_ID_PREFIX = '__CAVITY__';

/** Identify whether a roomId was synthesized from the wall-region builder. */
export function isSyntheticRoomId(roomId: string): boolean {
  return roomId.startsWith(OUTER_ROOM_ID_PREFIX) || roomId.startsWith(CAVITY_ROOM_ID_PREFIX);
}

/** Input vertex with position, room association, and adjacent edge directions */
export interface VertexInput {
  position: THREE.Vector3;
  roomId: string;
  /** Direction to previous vertex (normalized) */
  prevDirection: THREE.Vector2;
  /** Direction to next vertex (normalized) */
  nextDirection: THREE.Vector2;
  /** Length of the previous edge (vertex's incoming edge), in meters. */
  prevEdgeLength: number;
  /** Length of the next edge (vertex's outgoing edge), in meters. */
  nextEdgeLength: number;
  /**
   * Set for vertices that belong to a CCW polygon (the outer shell).
   * Without it the probe fan would shoot INTO the outdoor region
   * (the CCW interior); with it the math reverses prev/next and flips
   * the perpendicular so probes still travel INTO the wall material.
   */
  invertOutward?: boolean;
}

/** Input wall segment with start/end positions and room association */
export interface WallInput {
  start: THREE.Vector3;
  end: THREE.Vector3;
  roomId: string;
}

/** Result of closest point calculation on a segment */
export interface ClosestPointResult {
  closestPoint: THREE.Vector3;
  distance: number;
  /** Parameter t: 0 = start, 1 = end */
  t: number;
}

/** Configuration for the floor expansion algorithm */
export interface FloorExpansionConfig {
  /** Thickness of inner walls (between rooms) in meters */
  innerWallThickness: number;
  /** Thickness of outer walls in meters */
  outerWallThickness: number;
  /** Maximum ray cast distance - hits beyond this are considered outer walls */
  maxRayDistance: number;
  /** Distance threshold for merging close points after expansion */
  mergeTolerance: number;
  /** Maximum edge length for detecting small triangles to collapse */
  smallTriangleEdgeThreshold: number;
  /** Maximum sweep length per edge (meters); the probe sweep covers from the vertex up to min(edgeLen/2, this). */
  probeMaxWalk: number;
  /** Skip cleanup passes (when using external pipeline) */
  skipCleanupPasses?: boolean;
}

/** Default configuration values */
export const DEFAULT_EXPANSION_CONFIG: FloorExpansionConfig = {
  innerWallThickness: 0.15,
  outerWallThickness: 0.3,
  maxRayDistance: 2.0,
  mergeTolerance: 0.15,
  smallTriangleEdgeThreshold: 0.3,
  probeMaxWalk: 0.32,
};

/** Debug data for a single raycast */
export interface RaycastDebugData {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  distance: number;
  hasHit: boolean;
}

/** Debug data for multiple raycasts from a vertex */
export interface VertexRaycastsDebugData {
  /** All rays cast from this vertex */
  rays: RaycastDebugData[];
  /** Index of the ray that was used (shortest hit), or -1 if none */
  usedRayIndex: number;
}

/** A single edge probe: origin on the edge, perpendicular ray, optional hit, centerline midpoint. */
export interface ProbeDebugData {
  /** Probe origin (on the source edge, walked inward from the vertex). */
  origin: THREE.Vector3;
  /** Source edge direction (the centerline runs parallel to this). */
  edgeDirection: THREE.Vector3;
  /** Outward perpendicular direction the probe ray was cast along. */
  rayDirection: THREE.Vector3;
  /** Length of the probe ray (capped at maxRayDistance on a miss). */
  rayDistance: number;
  /** True if the probe hit another wall; false if we used the innerWallThickness fallback. */
  hasHit: boolean;
  /** The midpoint between origin and hit (or fallback) — sits on the wall centerline. */
  centerlineMidpoint: THREE.Vector3;
  /** True if this is the canonical (chosen) probe for its edge — the one whose centerline drives the intersection. */
  isChosen: boolean;
  /**
   * True for probes from the fallback "extended" pass that runs only
   * when the primary `[vertex → midpoint]` pass produced zero hits.
   * The debug renderer colors these distinctly so the user can see
   * which probes came from the extension vs the primary sweep.
   */
  isExtension: boolean;
  /**
   * Why this probe didn't land an accepted hit. `'no-intersection'`
   * when the ray hit nothing at all in range; the other three values
   * mean the closest candidate intersection was rejected by that
   * specific filter. Set even when `hasHit` is true (so consumers can
   * still see what would have filtered the *next-best* candidate);
   * the debug viz uses it only on misses.
   */
  missReason: RayMissReason;
}

/** Debug data for a single vertex expansion */
export interface VertexDebugData {
  originalPosition: THREE.Vector3;
  expandedPosition: THREE.Vector3;
  bisectorDirection: THREE.Vector3;
  isConvex: boolean;
  /** Raycast data for convex vertices */
  raycasts: VertexRaycastsDebugData | null;
  /** Per-edge probes used by the probe-based expansion (prev edge + next edge). */
  probes?: ProbeDebugData[];
  /** True if the vertex was skipped (centerlines parallel or intersection too far). */
  skipped?: boolean;
}

/** Debug data from skeleton generation */
export interface SkeletonDebugData {
  vertices: VertexDebugData[];
  /** Inflated wall-region components used to anchor outer walls and cavities. */
  wallRegion: WallRegionComponent[];
  /** All room polygons by id (real rooms + synthetic outer/cavity), for resolving source-vertex positions. */
  roomPolygonsById: Map<string, THREE.Vector3[]>;
}

/** Result from skeleton generation */
export interface GenerateSkeletonResult {
  skeleton: FloorSkeleton;
  debugData: SkeletonDebugData;
}

/**
 * Find the closest point on a line segment to a given point
 */
export function closestPointOnSegment(point: THREE.Vector3, segStart: THREE.Vector3, segEnd: THREE.Vector3): ClosestPointResult {
  const line = new THREE.Vector3().subVectors(segEnd, segStart);
  const lineLength = line.length();

  if (lineLength === 0) {
    return { closestPoint: segStart.clone(), distance: point.distanceTo(segStart), t: 0 };
  }

  const lineDir = line.clone().normalize();
  const pointToStart = new THREE.Vector3().subVectors(point, segStart);

  let t = pointToStart.dot(lineDir) / lineLength;
  t = Math.max(0, Math.min(1, t));

  const closestPoint = new THREE.Vector3().addVectors(segStart, lineDir.multiplyScalar(t * lineLength));
  const distance = point.distanceTo(closestPoint);

  return { closestPoint, distance, t };
}

/**
 * Find a node at a given position within the skeleton
 */
export function findNodeAtPosition(skeleton: FloorSkeleton, position: THREE.Vector3, tolerance: number): SkeletonNode | null {
  for (const node of skeleton.nodes.values()) {
    if (node.position.distanceTo(position) < tolerance) {
      return node;
    }
  }
  return null;
}

/**
 * Find an edge between two nodes in the skeleton
 */
export function findEdgeBetweenNodes(skeleton: FloorSkeleton, nodeId1: string, nodeId2: string): SkeletonEdge | null {
  for (const edge of skeleton.edges.values()) {
    if ((edge.startNodeId === nodeId1 && edge.endNodeId === nodeId2) || (edge.startNodeId === nodeId2 && edge.endNodeId === nodeId1)) {
      return edge;
    }
  }
  return null;
}

/**
 * Calculate the cross product of two 2D vectors (returns scalar z-component)
 */
function cross2D(a: THREE.Vector2, b: THREE.Vector2): number {
  return a.x * b.y - a.y * b.x;
}

/**
 * Check if a vertex is convex (pointing outward) or concave (pointing inward)
 * For a clockwise polygon, convex vertices have negative cross product
 */
function isVertexConvex(prevDir: THREE.Vector2, nextDir: THREE.Vector2): boolean {
  // Cross product of incoming and outgoing edge directions
  // For CW polygons: Negative = convex (outer corner), Positive = concave (inner corner)
  return cross2D(prevDir, nextDir) <= 0;
}

/**
 * Ray-segment intersection in 2D
 * Returns the distance along the ray to the intersection point, or null if no intersection
 */
function raySegmentIntersection2D(rayOrigin: THREE.Vector2, rayDir: THREE.Vector2, segStart: THREE.Vector2, segEnd: THREE.Vector2): number | null {
  const segDir = new THREE.Vector2().subVectors(segEnd, segStart);
  const segLength = segDir.length();

  if (segLength < 0.0001) return null;

  segDir.normalize();

  // Solve: rayOrigin + t * rayDir = segStart + s * segDir
  // Using cross product method
  const denominator = cross2D(rayDir, segDir);

  if (Math.abs(denominator) < 0.0001) {
    // Parallel lines
    return null;
  }

  const diff = new THREE.Vector2().subVectors(segStart, rayOrigin);
  const t = cross2D(diff, segDir) / denominator;
  const s = cross2D(diff, rayDir) / denominator;

  // t must be positive (ray goes forward).
  // s must lie on the segment, with a small tolerance at each end so
  // parallel rays grazing the endpoint of a slanted wall don't
  // arbitrarily report `no-intersection` while a sister ray from a
  // few cm away reports a hit. Without this tolerance, "same source
  // edge, same target wall, different probe origin → different
  // accept" is unavoidable for finite wall segments.
  const SEGMENT_BOUND_TOLERANCE = 0.05; // 5 cm of slack at each end
  if (t > 0.001 && s >= -SEGMENT_BOUND_TOLERANCE && s <= segLength + SEGMENT_BOUND_TOLERANCE) {
    return t;
  }

  return null;
}

/** Wall segment with index for identification */
interface WallWithIndex {
  start: THREE.Vector2;
  end: THREE.Vector2;
  roomId: string;
  /** Global wall index across all rooms. */
  index: number;
  /** Local edge index within the source room's polygon. */
  edgeIndexInRoom: number;
  /**
   * Unit normal pointing toward the wall-material side of this wall.
   * Computed once as `(-dy, dx)` (left-of-travel), which works
   * uniformly because of how the polygon windings line up with the
   * user's "wall material" intuition:
   *  - CW rooms / cavities → left-of-travel = OUTSIDE the polygon
   *    = outside the room / outside the cavity void = wall material.
   *  - CCW outer shells    → left-of-travel = INSIDE the polygon
   *    = inside the outdoor region surrounding the building, which
   *    is the wall-material side from the user's framing
   *    ("rays should hit the outer shell from inside the shell").
   * A hit is accepted iff the ray origin is on this side, so the
   * same `offset > 0` test serves every polygon kind — no branch in
   * the inner cast loop.
   */
  materialSideNormal: THREE.Vector2;
}

/**
 * Calculate perpendicular distance from a point to an infinite line defined by two points
 */
function perpendicularDistanceToLine(point: THREE.Vector2, lineStart: THREE.Vector2, lineEnd: THREE.Vector2): number {
  const lineDir = new THREE.Vector2().subVectors(lineEnd, lineStart);
  const lineLength = lineDir.length();
  if (lineLength < 0.0001) return point.distanceTo(lineStart);

  // Perpendicular distance = |cross(lineDir, pointToStart)| / |lineDir|
  const pointToStart = new THREE.Vector2().subVectors(point, lineStart);
  const crossProduct = Math.abs(cross2D(lineDir, pointToStart));
  return crossProduct / lineLength;
}

/**
 * Cast a single ray and find the shortest hit distance
 * @param origin Ray origin
 * @param direction Ray direction (normalized)
 * @param allWalls All wall segments to test against
 * @param skipWallIndices Indices of walls to skip (typically the two adjacent edges at the vertex)
 * @param maxDistance Maximum ray distance
 * @returns Object with ray distance and perpendicular distance to the hit wall
 */
/**
 * Cosine of the maximum allowed angle between the incoming ray and the
 * wall's normal. Hits that strike the wall at more than 45° off
 * perpendicular are rejected — those are typically grazing hits across
 * a corner or a far-away wall the probe wasn't really aimed at, and
 * including them produces unstable centerline midpoints.
 */
const MAX_HIT_ANGLE_OFF_PERPENDICULAR_DEG = 45;
const MIN_HIT_ALIGNMENT_COS = Math.cos((MAX_HIT_ANGLE_OFF_PERPENDICULAR_DEG * Math.PI) / 180);

/**
 * Reason a candidate ray-hit was rejected. Surfaced on a per-probe
 * basis so the debug viz can color-code misses by cause and the user
 * can tell at a glance which filter ate the visually-obvious hit.
 *
 * Tracked on the CLOSEST candidate intersection (so the reason
 * describes whatever's blocking the nearest wall, not some far
 * coincidental rejection). Values:
 *  - `no-intersection`: the ray didn't touch any wall segment at all,
 *    NOT even one we ended up skipping. Rare in practice.
 *  - `only-skipped`: every intersection along the ray was on a wall
 *    in `skipWallIndices` (the vertex's own adjacent edges). This
 *    happens when the outward direction at a tight corner crosses an
 *    adjacent edge before reaching anything else — the ray visually
 *    passes through wall material but the next non-skipped polygon
 *    edge is too far away or not on the ray.
 *  - `too-far`: a candidate intersection existed but was farther than
 *    `maxRayDistance`.
 *  - `wrong-side`: a candidate intersection existed but the ray
 *    origin sat on the wall's non-material side (inside the room /
 *    cavity, or outside the outer shell).
 *  - `too-oblique`: a candidate intersection existed but the angle
 *    of incidence exceeded 45° off perpendicular.
 */
export type RayMissReason = 'no-intersection' | 'only-skipped' | 'too-far' | 'wrong-side' | 'too-oblique';

function castSingleRay(
  origin: THREE.Vector2,
  direction: THREE.Vector2,
  allWalls: WallWithIndex[],
  skipWallIndices: Set<number>,
  maxDistance: number,
  /**
   * Reference point for the side-filter test. Defaults to `origin`,
   * but callers running a sweep along an edge pass the sweep's
   * VERTEX position so the side check is consistent across all
   * probes in that sweep. Different probe origins along the same
   * source edge would otherwise project to different signed offsets
   * relative to a non-parallel target wall — producing the
   * "same edge, same target, different result depending on probe
   * starting position" bug.
   */
  sideRefPoint: THREE.Vector2 = origin,
): {
  distance: number | null;
  perpendicularDistance: number | null;
  wallNormal: THREE.Vector2 | null;
  hitWall: WallWithIndex | null;
  /**
   * When `distance === null`, why was the candidate rejected. Picks
   * the reason of the FARTHEST rejected intersection along the ray —
   * i.e. the last wall the ray crossed before running out of reach.
   * That's the filter that actually "stopped" the ray and is the most
   * informative for the debug viz: a probe whose nearest crossing was
   * a `wrong-side` skip but kept going and ultimately got cut by
   * `too-far` reports `too-far`, since extending the ray would have
   * found nothing closer anyway.
   *
   * `'no-intersection'` when the ray didn't touch any wall at all.
   */
  missReason: RayMissReason;
  /** Distance to the farthest rejected hit (debug-viz aid). Null when nothing intersected. */
  missDistance: number | null;
} {
  let shortestDistance: number | null = null;
  let hitWall: WallWithIndex | null = null;

  // Track the FARTHEST rejected intersection across the loop. Reason
  // describes the last filter the ray tripped on before failing to
  // land an accepted hit — i.e. what would need to change for the
  // ray to find something further out.
  let farthestRejectDist = -Infinity;
  let farthestRejectReason: RayMissReason = 'no-intersection';
  const recordReject = (dist: number, reason: RayMissReason): void => {
    if (dist > farthestRejectDist) {
      farthestRejectDist = dist;
      farthestRejectReason = reason;
    }
  };

  for (const wall of allWalls) {
    // For skipped walls (the vertex's own adjacent edges) we still
    // need to test for an intersection to detect the "only-skipped"
    // case — a ray whose closest crossing is on a skipped wall would
    // otherwise come back as `no-intersection`, falsely suggesting
    // the ray fired into empty space.
    const isSkipped = skipWallIndices.has(wall.index);
    const distance = raySegmentIntersection2D(origin, direction, wall.start, wall.end);
    if (distance === null) continue;
    if (isSkipped) {
      recordReject(distance, 'only-skipped');
      continue;
    }
    if (distance > maxDistance) {
      recordReject(distance, 'too-far');
      continue;
    }

    // Side rule: the ray must approach the wall from the wall-material
    // side. `materialSideNormal` points into the wall material; the ray
    // origin must lie on that side (positive offset from a point on the
    // wall). Rays from a room's vertex therefore can only land on:
    //  - other rooms' walls if the origin is outside that room,
    //  - outer-shell walls if the origin is inside the shell (i.e. in
    //    the outdoor region surrounding the building),
    //  - cavity walls if the origin is outside that cavity.
    // Without this rule, a probe inside one room would happily hit the
    // far wall of the adjacent room "from inside it," yielding an
    // implausibly long centerline.
    //
    // The threshold is `-SIDE_EPS` rather than 0 to absorb numerical
    // noise where a probe origin sits exactly on (or microscopically
    // past) a wall's infinite line — which happens routinely whenever
    // two walls are collinear (parallel walls along a shared axis).
    // Without the epsilon, hits flicker on/off along such a line as
    // float rounding wobbles around zero, producing the "same kind of
    // ray sometimes does and sometimes doesn't hit" flake.
    // Side check uses `sideRefPoint` (the sweep's vertex) instead of
    // the per-probe `origin` so all probes in a sweep get the same
    // accept/reject decision against any given target wall. With
    // `origin`, probes along a slanted source edge project to
    // different signed offsets on a non-parallel target wall and
    // flicker pass/fail across the sweep — even though they all hit
    // the same wall along the same direction.
    const rx = sideRefPoint.x - wall.start.x;
    const ry = sideRefPoint.y - wall.start.y;
    const offset = rx * wall.materialSideNormal.x + ry * wall.materialSideNormal.y;
    const SIDE_EPS = 1e-4;
    if (offset < -SIDE_EPS) {
      recordReject(distance, 'wrong-side');
      continue;
    }

    // Angle rule: hits at more than 45° off perpendicular are
    // discarded. `direction` points away from the origin, so the ray's
    // incidence on the wall is `-direction`; that must align with the
    // material-side normal within the threshold. Threshold is on the
    // cosine so the comparison is one multiply-add.
    const alignment = -direction.x * wall.materialSideNormal.x - direction.y * wall.materialSideNormal.y;
    if (alignment < MIN_HIT_ALIGNMENT_COS) {
      recordReject(distance, 'too-oblique');
      continue;
    }

    if (shortestDistance === null || distance < shortestDistance) {
      shortestDistance = distance;
      hitWall = wall;
    }
  }

  // Calculate perpendicular distance and wall normal
  let perpendicularDistance: number | null = null;
  let wallNormal: THREE.Vector2 | null = null;
  if (hitWall !== null) {
    perpendicularDistance = perpendicularDistanceToLine(origin, hitWall.start, hitWall.end);

    // Calculate wall normal pointing toward the origin
    const wallDir = new THREE.Vector2().subVectors(hitWall.end, hitWall.start).normalize();
    // Perpendicular to wall direction
    const normal1 = new THREE.Vector2(-wallDir.y, wallDir.x);
    // Check which direction points toward origin
    const toOrigin = new THREE.Vector2().subVectors(origin, hitWall.start);
    if (normal1.dot(toOrigin) > 0) {
      wallNormal = normal1;
    } else {
      wallNormal = new THREE.Vector2(wallDir.y, -wallDir.x);
    }
  }

  return {
    distance: shortestDistance,
    perpendicularDistance,
    wallNormal,
    hitWall,
    missReason: farthestRejectReason,
    missDistance: farthestRejectDist === -Infinity ? null : farthestRejectDist,
  };
}

/** A polygon edge that the chosen probe for one side of a vertex hit. */
export interface ProbedHit {
  /** Which adjacent edge of the source vertex produced this hit. */
  sourceEdgeKind: 'prev' | 'next';
  /** Room (or synthetic outer/cavity) that owns the hit polygon edge. */
  hitRoomId: string;
  /** Index of the hit edge within that polygon. */
  hitEdgeIndex: number;
}

/** Result from expanding a vertex */
interface ExpandVertexResult {
  position: THREE.Vector3;
  debugData: VertexDebugData;
  /** Walls hit by the chosen probes on each side of the vertex (only successful hits are included). */
  probedHits: ProbedHit[];
}

/**
 * Expand a single vertex outward based on ray casting results
 * @param vertex The vertex to expand
 * @param allWalls All wall segments with indices
 * @param skipWallIndices Indices of walls to skip (adjacent edges at this vertex)
 * @param config Expansion configuration
 */
/** Number of probes cast per adjacent edge, evenly spaced from vertex to edge midpoint. */
const PROBES_PER_EDGE = 5;
/** Stop the probe sweep when a probe's hit distance grows by more than this fraction vs. the previous probe. */
const PROBE_GROWTH_THRESHOLD = 0.02;

/**
 * Extension-pass parameters. The fallback sweep that runs only when
 * the primary `[vertex → midpoint]` pass produced zero hits uses a
 * fixed step + geometric growth rather than evenly-dividing the edge,
 * so:
 *  - The probe layout is decoupled from edge length (one short edge
 *    next to one long edge would otherwise cluster very differently).
 *  - Each successive probe reaches further out than the last, so the
 *    extension does a real "search outward" instead of evenly tiling
 *    a fixed band.
 *
 * Step i sits at distance `EXTENSION_BASE_STEP * EXTENSION_GROWTH^i`
 * past the original sweep boundary. With base = 0.1 m and growth = 1.5,
 * the offsets are roughly 0.10, 0.15, 0.22, 0.34, 0.51 m — covering
 * 1.3 m total reach on each side from `PROBES_PER_EDGE = 5` probes.
 */
const EXTENSION_BASE_STEP = 0.1;
const EXTENSION_GROWTH = 1.5;

/** Result of a probe sweep along one adjacent edge. */
interface ProbeSweepResult {
  /** Origin (along edge) of the canonical probe — last good one before stop. */
  chosenOrigin: THREE.Vector2;
  /** Hit distance of the canonical probe, or null if all probes missed. */
  chosenHitDist: number | null;
  /** Wall the chosen probe hit (roomId + edge index in that polygon), or null on miss. */
  chosenHitWall: { roomId: string; edgeIndexInRoom: number } | null;
  /** Debug entries for every probe attempted (so the viz shows the full sweep). */
  probeDebug: ProbeDebugData[];
}

/**
 * Cast PROBES_PER_EDGE perpendicular probes evenly spaced from `vertexPos`
 * (probe 0) to the edge midpoint (probe N-1). Iterate in order; stop at the
 * first probe that misses or grows by >2% vs. the previous one. Use the last
 * probe BEFORE the jump as the canonical result.
 *
 * Fallback for full-miss sweeps: if no probe in the initial span landed a
 * hit, run a SECOND pass extending from the midpoint to the far end of
 * the edge. Door / window jambs and other openings at the vertex side
 * of the wall can leave probes 0..N-1 firing into empty space; the
 * extension picks up the wall material that's still there past the
 * opening. Gated on a full miss so existing hit cases (where the
 * growth-stop rule already trims at openings) are unchanged.
 */
function sweepEdgeProbes(
  vertexPos: THREE.Vector2,
  edgeDir: THREE.Vector2,
  edgeLen: number,
  edgeWalkSign: 1 | -1,
  outward: THREE.Vector2,
  allWalls: WallWithIndex[],
  skipWallIndices: Set<number>,
  maxRayDistance: number,
  innerWallHalfThickness: number,
  _probeMaxWalk: number,
): ProbeSweepResult {
  const probeDebug: ProbeDebugData[] = [];

  // Side-filter reference point for this sweep. Computed as the edge
  // MIDPOINT so that both endpoints of the same source edge produce
  // the same accept/reject decision against any given target wall.
  // Using `vertexPos` would make the side check depend on which
  // endpoint started the sweep — two probes from opposite ends of
  // the same edge would otherwise see different signs against a
  // non-parallel target wall and produce inconsistent acceptance.
  const sideRefPoint = new THREE.Vector2(
    vertexPos.x + edgeDir.x * (edgeLen / 2) * edgeWalkSign,
    vertexPos.y + edgeDir.y * (edgeLen / 2) * edgeWalkSign,
  );

  const state = {
    chosenIndex: -1,
    chosenWalk: 0,
    chosenHitDist: null as number | null,
    chosenHitWall: null as { roomId: string; edgeIndexInRoom: number } | null,
  };

  /**
   * Run one PROBES_PER_EDGE-long sweep over walk distances in
   * `[startWalk, endWalk]`. Mutates `state` to record the best hit seen
   * so far (across this AND prior calls in the same invocation) and
   * appends one ProbeDebugData per probe to `probeDebug`.
   *
   * Returns true iff this pass landed at least one accepted hit.
   */
  const runPass = (startWalk: number, endWalk: number, indexOffset: number, isExtension: boolean): boolean => {
    let prevHitDist: number | null = null;
    let stopped = false;
    let landedHit = false;
    const span = endWalk - startWalk;

    for (let i = 0; i < PROBES_PER_EDGE; i++) {
      const t = i / (PROBES_PER_EDGE - 1);
      const walk = startWalk + span * t;
      const ox = vertexPos.x + edgeDir.x * walk * edgeWalkSign;
      const oy = vertexPos.y + edgeDir.y * walk * edgeWalkSign;
      const origin2D = new THREE.Vector2(ox, oy);

      let hitDist: number | null = null;
      // Probes that don't actually cast (post-growth-stop) get
      // `'no-intersection'` since there's nothing to reject.
      let missReason: RayMissReason = 'no-intersection';
      if (!stopped) {
        const probe = castSingleRay(origin2D, outward, allWalls, skipWallIndices, maxRayDistance, sideRefPoint);
        hitDist = probe.distance;
        missReason = probe.missReason;

        const grewTooMuch = hitDist === null || (prevHitDist !== null && hitDist > prevHitDist * (1 + PROBE_GROWTH_THRESHOLD));
        if (i === 0) {
          // Probe 0 sets the baseline for this pass. If it hits, it's
          // the current best (across passes).
          if (hitDist !== null && probe.hitWall) {
            state.chosenIndex = indexOffset + i;
            state.chosenWalk = walk;
            state.chosenHitDist = hitDist;
            state.chosenHitWall = { roomId: probe.hitWall.roomId, edgeIndexInRoom: probe.hitWall.edgeIndexInRoom };
            landedHit = true;
          }
        } else if (grewTooMuch) {
          stopped = true;
        } else if (hitDist !== null && probe.hitWall) {
          state.chosenIndex = indexOffset + i;
          state.chosenWalk = walk;
          state.chosenHitDist = hitDist;
          state.chosenHitWall = { roomId: probe.hitWall.roomId, edgeIndexInRoom: probe.hitWall.edgeIndexInRoom };
          landedHit = true;
        }
        if (!stopped) prevHitDist = hitDist;
      }

      const debugHalf = hitDist !== null ? hitDist / 2 : innerWallHalfThickness;
      const debugMid = new THREE.Vector2(ox + outward.x * debugHalf, oy + outward.y * debugHalf);
      probeDebug.push({
        origin: new THREE.Vector3(ox, oy, 0),
        edgeDirection: new THREE.Vector3(edgeDir.x, edgeDir.y, 0),
        rayDirection: new THREE.Vector3(outward.x, outward.y, 0),
        rayDistance: hitDist ?? maxRayDistance,
        hasHit: hitDist !== null,
        centerlineMidpoint: new THREE.Vector3(debugMid.x, debugMid.y, 0),
        isChosen: false,
        isExtension,
        missReason,
      });
    }

    return landedHit;
  };

  /**
   * Cast a single probe at an absolute walk distance and record it in
   * `probeDebug`. Used by the extension passes, which need a custom
   * (non-linear) distribution of probe origins along the edge line
   * and therefore can't reuse `runPass`'s evenly-spaced loop.
   *
   * Unlike `runPass`, this helper has no growth-stop / monotonic-hit
   * heuristics. The extension is explicitly searching past gaps and
   * openings, so applying growth-stop here would defeat its purpose.
   *
   * Updates `state` whenever the probe lands an accepted hit AND the
   * walk distance is closer to the original sweep boundary than the
   * current `state.chosenWalk` — i.e. extension hits prefer the
   * nearest extension probe over a farther one.
   */
  const castOneProbe = (walk: number, debugIndex: number): boolean => {
    const ox = vertexPos.x + edgeDir.x * walk * edgeWalkSign;
    const oy = vertexPos.y + edgeDir.y * walk * edgeWalkSign;
    const origin2D = new THREE.Vector2(ox, oy);
    const probe = castSingleRay(origin2D, outward, allWalls, skipWallIndices, maxRayDistance, sideRefPoint);
    const hitDist = probe.distance;

    let landed = false;
    if (hitDist !== null && probe.hitWall && state.chosenIndex < 0) {
      state.chosenIndex = debugIndex;
      state.chosenWalk = walk;
      state.chosenHitDist = hitDist;
      state.chosenHitWall = { roomId: probe.hitWall.roomId, edgeIndexInRoom: probe.hitWall.edgeIndexInRoom };
      landed = true;
    }

    const debugHalf = hitDist !== null ? hitDist / 2 : innerWallHalfThickness;
    const debugMid = new THREE.Vector2(ox + outward.x * debugHalf, oy + outward.y * debugHalf);
    probeDebug.push({
      origin: new THREE.Vector3(ox, oy, 0),
      edgeDirection: new THREE.Vector3(edgeDir.x, edgeDir.y, 0),
      rayDirection: new THREE.Vector3(outward.x, outward.y, 0),
      rayDistance: hitDist ?? maxRayDistance,
      hasHit: hitDist !== null,
      centerlineMidpoint: new THREE.Vector3(debugMid.x, debugMid.y, 0),
      isChosen: false,
      isExtension: true,
      missReason: probe.missReason,
    });
    return landed;
  };

  // First pass: vertex → midpoint, same as before.
  const midpointWalk = edgeLen / 2;
  const firstHit = runPass(0, midpointWalk, 0, /* isExtension */ false);

  // Fallback: full miss → cast extension probes along the EDGE LINE,
  // past both endpoints. Spacing is decoupled from the edge length and
  // uses a geometric stride from `EXTENSION_BASE_STEP` outward, so
  // step i sits at `BASE * GROWTH^i` past the boundary. Each successive
  // probe reaches further than the last — the extension is a search
  // outward, not an even-tile of a band.
  //
  // Only runs when the primary pass produced zero hits — keeps the
  // "extension is a recovery mechanism" semantic.
  if (!firstHit) {
    // Probe-origin offsets (in meters) measured from the edge's
    // boundary on each side. Built once and reused: -side uses these
    // values negated and added to walk=0; +side uses them added to
    // walk=edgeLen. The first hit anywhere wins (see `castOneProbe`,
    // which only writes to `state` when `chosenIndex < 0`).
    const stepOffsets: number[] = [];
    {
      let step = EXTENSION_BASE_STEP;
      for (let i = 0; i < PROBES_PER_EDGE; i++) {
        stepOffsets.push(step);
        step *= EXTENSION_GROWTH;
      }
    }
    // Past the vertex (negative direction along the edge).
    for (let i = 0; i < stepOffsets.length; i++) {
      castOneProbe(-stepOffsets[i], PROBES_PER_EDGE + i);
    }
    // Past the far endpoint.
    for (let i = 0; i < stepOffsets.length; i++) {
      castOneProbe(edgeLen + stepOffsets[i], PROBES_PER_EDGE * 2 + i);
    }
  }

  // Resolve chosen origin from the recorded walk distance, or default
  // to the vertex when every probe across every pass missed.
  const chosenOrigin = new THREE.Vector2(vertexPos.x + edgeDir.x * state.chosenWalk * edgeWalkSign, vertexPos.y + edgeDir.y * state.chosenWalk * edgeWalkSign);

  if (state.chosenIndex >= 0 && state.chosenIndex < probeDebug.length) {
    probeDebug[state.chosenIndex].isChosen = true;
  }

  return { chosenOrigin, chosenHitDist: state.chosenHitDist, chosenHitWall: state.chosenHitWall, probeDebug };
}

function expandVertex(vertex: VertexInput, allWalls: WallWithIndex[], skipWallIndices: Set<number>, config: FloorExpansionConfig, nodeIndex: number): ExpandVertexResult {
  const pos2D = new THREE.Vector2(vertex.position.x, vertex.position.y);

  // For CCW outer-shell polygons, reverse the polygon traversal locally so the
  // same CW-oriented math points the perpendicular probes toward the wall
  // material. Reversal at a vertex = swap prev/next AND negate both directions
  // (the "incoming" edge becomes the formerly-outgoing edge walked backwards).
  const prevDir = vertex.invertOutward ? new THREE.Vector2(-vertex.nextDirection.x, -vertex.nextDirection.y) : vertex.prevDirection;
  const nextDir = vertex.invertOutward ? new THREE.Vector2(-vertex.prevDirection.x, -vertex.prevDirection.y) : vertex.nextDirection;
  const prevLen = vertex.invertOutward ? vertex.nextEdgeLength : vertex.prevEdgeLength;
  const nextLen = vertex.invertOutward ? vertex.prevEdgeLength : vertex.nextEdgeLength;

  const isConvex = isVertexConvex(prevDir, nextDir);

  // Outward perpendicular (CW-room convention): (-dir.y, dir.x) is
  // left-of-travel = away from CW interior. For outer-shell vertices
  // we already negated prev/next above to locally reverse the
  // traversal, which flips this perpendicular too — flip it back so
  // it still points INTO the wall material.
  const perpSign = vertex.invertOutward ? -1 : 1;
  const prevOutward = new THREE.Vector2(-prevDir.y * perpSign, prevDir.x * perpSign);
  const nextOutward = new THREE.Vector2(-nextDir.y * perpSign, nextDir.x * perpSign);

  // Sweep probes along each adjacent edge from the vertex toward the midpoint.
  // prevEdge runs prev->curr (direction = prevDir), so walking from curr toward
  // the midpoint of the prev edge means going in -prevDir → edgeWalkSign = -1.
  // nextEdge runs curr->next → going in +nextDir → edgeWalkSign = +1.
  const innerHalf = config.innerWallThickness / 2;
  const prevSweep = sweepEdgeProbes(pos2D, prevDir, prevLen, -1, prevOutward, allWalls, skipWallIndices, config.maxRayDistance, innerHalf, config.probeMaxWalk);
  const nextSweep = sweepEdgeProbes(pos2D, nextDir, nextLen, +1, nextOutward, allWalls, skipWallIndices, config.maxRayDistance, innerHalf, config.probeMaxWalk);

  // Centerline midpoint for each edge: half the chosen probe's hit, or innerWallThickness/2 on full miss.
  const prevHalf = prevSweep.chosenHitDist !== null ? prevSweep.chosenHitDist / 2 : innerHalf;
  const nextHalf = nextSweep.chosenHitDist !== null ? nextSweep.chosenHitDist / 2 : innerHalf;

  const prevMid = new THREE.Vector2(prevSweep.chosenOrigin.x + prevOutward.x * prevHalf, prevSweep.chosenOrigin.y + prevOutward.y * prevHalf);
  const nextMid = new THREE.Vector2(nextSweep.chosenOrigin.x + nextOutward.x * nextHalf, nextSweep.chosenOrigin.y + nextOutward.y * nextHalf);

  // Two centerlines, each parallel to its source edge through the midpoint.
  // Solve for intersection.
  // Line normal form: n . X = n . origin where n is perpendicular to edge dir.
  const n1x = -prevDir.y;
  const n1y = prevDir.x;
  const c1 = n1x * prevMid.x + n1y * prevMid.y;
  const n2x = -nextDir.y;
  const n2y = nextDir.x;
  const c2 = n2x * nextMid.x + n2y * nextMid.y;
  const det = n1x * n2y - n1y * n2x;

  // Aggregate debug entries from both edge sweeps.
  const probesDebug: ProbeDebugData[] = [...prevSweep.probeDebug, ...nextSweep.probeDebug];

  // Surface the chosen-probe hits so each node can record which polygon
  // edges it was actually built from. Misses are skipped — the array is
  // empty when neither sweep landed a hit.
  const probedHits: ProbedHit[] = [];
  if (prevSweep.chosenHitWall) {
    probedHits.push({ sourceEdgeKind: 'prev', hitRoomId: prevSweep.chosenHitWall.roomId, hitEdgeIndex: prevSweep.chosenHitWall.edgeIndexInRoom });
  }
  if (nextSweep.chosenHitWall) {
    probedHits.push({ sourceEdgeKind: 'next', hitRoomId: nextSweep.chosenHitWall.roomId, hitEdgeIndex: nextSweep.chosenHitWall.edgeIndexInRoom });
  }

  // Compute the corner's outward bisector explicitly from the two edge
  // perpendiculars. This is independent of where the expansion lands, so the
  // snap-to-original-vertex rule has a stable angular reference even when
  // the expanded position is very close to the original vertex (or skipped).
  const bisRaw = new THREE.Vector2(prevOutward.x + nextOutward.x, prevOutward.y + nextOutward.y);
  const bisLen = bisRaw.length();
  const bisDir = bisLen > 1e-6 ? new THREE.Vector3(bisRaw.x / bisLen, bisRaw.y / bisLen, 0) : new THREE.Vector3(0, 0, 0);

  const buildDebug = (expandedPos: THREE.Vector3, skipped: boolean): VertexDebugData => ({
    originalPosition: vertex.position.clone(),
    expandedPosition: expandedPos.clone(),
    bisectorDirection: bisDir.clone(),
    isConvex,
    raycasts: null,
    probes: probesDebug,
    skipped,
  });

  // Skip when centerlines are near-parallel.
  if (Math.abs(det) < 1e-4) {
    const fallback = vertex.position.clone();
    return { position: fallback, debugData: buildDebug(fallback, true), probedHits };
  }

  const ix = (c1 * n2y - c2 * n1y) / det;
  const iy = (n1x * c2 - n2x * c1) / det;

  // Skip if intersection is too far from the original vertex.
  const dx = ix - pos2D.x;
  const dy = iy - pos2D.y;
  if (Math.hypot(dx, dy) > config.maxRayDistance) {
    const fallback = vertex.position.clone();
    return { position: fallback, debugData: buildDebug(fallback, true), probedHits };
  }

  const expandedPos = new THREE.Vector3(ix, iy, vertex.position.z);
  return { position: expandedPos, debugData: buildDebug(expandedPos, false), probedHits };
}

/**
 * Merge nearby expanded vertices and create skeleton nodes
 */
function createNodesFromExpandedVertices(
  skeleton: FloorSkeleton,
  expandedVertices: Array<{ position: THREE.Vector3; roomId: string; originalIndex: number; vertexIndexInRoom: number; probedHits: SkeletonProbedHit[] }>,
  mergeTolerance: number,
): Map<number, string> {
  // Maps original vertex index to node ID
  const vertexToNodeMap = new Map<number, string>();
  let nodeIdCounter = 0;

  for (const vertex of expandedVertices) {
    // Check if there's already a node close to this position
    let existingNode: SkeletonNode | null = null;

    for (const node of skeleton.nodes.values()) {
      const dist = node.position.distanceTo(vertex.position);
      if (dist < mergeTolerance) {
        existingNode = node;
        break;
      }
    }

    if (existingNode) {
      // Merge with existing node - update position to centroid
      const count = existingNode.roomIds.length + 1;
      existingNode.position.lerp(vertex.position, 1 / count);

      if (!existingNode.roomIds.includes(vertex.roomId)) {
        existingNode.roomIds.push(vertex.roomId);
      }
      existingNode.sourceVertices.push({ roomId: vertex.roomId, vertexIndex: vertex.vertexIndexInRoom });
      for (const hit of vertex.probedHits) existingNode.probedHits.push(hit);

      vertexToNodeMap.set(vertex.originalIndex, existingNode.id);
    } else {
      // Create new node
      const nodeId = `node_${nodeIdCounter++}`;
      const node: SkeletonNode = {
        id: nodeId,
        position: vertex.position.clone(),
        roomIds: [vertex.roomId],
        edgeIds: [],
        sourceVertices: [{ roomId: vertex.roomId, vertexIndex: vertex.vertexIndexInRoom }],
        probedHits: vertex.probedHits.slice(),
        selected: false,
      };
      skeleton.nodes.set(nodeId, node);
      vertexToNodeMap.set(vertex.originalIndex, nodeId);
    }
  }

  return vertexToNodeMap;
}

/**
 * Create edges between nodes based on original room connectivity
 */
function createEdgesFromConnectivity(skeleton: FloorSkeleton, rooms: Array<{ identifier: string; polygon: THREE.Vector3[]; vertexStartIndex: number }>, vertexToNodeMap: Map<number, string>): void {
  let edgeIdCounter = 0;

  for (const room of rooms) {
    const vertexCount = room.polygon.length;

    for (let i = 0; i < vertexCount; i++) {
      const currentIndex = room.vertexStartIndex + i;
      const nextIndex = room.vertexStartIndex + ((i + 1) % vertexCount);

      const startNodeId = vertexToNodeMap.get(currentIndex);
      const endNodeId = vertexToNodeMap.get(nextIndex);

      if (!startNodeId || !endNodeId || startNodeId === endNodeId) continue;

      // Check if edge already exists
      const existingEdge = findEdgeBetweenNodes(skeleton, startNodeId, endNodeId);

      if (existingEdge) {
        if (!existingEdge.roomIds.includes(room.identifier)) {
          existingEdge.roomIds.push(room.identifier);
        }
      } else {
        const edgeId = `edge_${edgeIdCounter++}`;
        const edge: SkeletonEdge = {
          id: edgeId,
          startNodeId,
          endNodeId,
          roomIds: [room.identifier],
          leftSize: 0.1,
          rightSize: 0.1,
          wallType: 'INNER',
          selected: false,
        };
        skeleton.edges.set(edgeId, edge);

        const startNode = skeleton.nodes.get(startNodeId);
        const endNode = skeleton.nodes.get(endNodeId);
        if (startNode) startNode.edgeIds.push(edgeId);
        if (endNode) endNode.edgeIds.push(edgeId);
      }
    }
  }
}

/**
 * Check if two line segments intersect (2D, ignoring Z)
 * Returns the intersection point if they intersect, null otherwise
 */
function segmentIntersection2D(a1: THREE.Vector3, a2: THREE.Vector3, b1: THREE.Vector3, b2: THREE.Vector3): THREE.Vector3 | null {
  const d1x = a2.x - a1.x;
  const d1y = a2.y - a1.y;
  const d2x = b2.x - b1.x;
  const d2y = b2.y - b1.y;

  const cross = d1x * d2y - d1y * d2x;

  // Parallel lines
  if (Math.abs(cross) < 0.0001) return null;

  const dx = b1.x - a1.x;
  const dy = b1.y - a1.y;

  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  // Check if intersection is within both segments (excluding endpoints)
  // Use a smaller epsilon to catch more intersections near endpoints
  const epsilon = 0.001;
  if (t > epsilon && t < 1 - epsilon && u > epsilon && u < 1 - epsilon) {
    return new THREE.Vector3(a1.x + t * d1x, a1.y + t * d1y, a1.z);
  }

  return null;
}

/**
 * Find all edge crossings in the skeleton
 */
function findEdgeCrossings(skeleton: FloorSkeleton): Array<{ edge1Id: string; edge2Id: string; intersection: THREE.Vector3 }> {
  const crossings: Array<{ edge1Id: string; edge2Id: string; intersection: THREE.Vector3 }> = [];
  const edgeIds = Array.from(skeleton.edges.keys());

  for (let i = 0; i < edgeIds.length; i++) {
    for (let j = i + 1; j < edgeIds.length; j++) {
      const edge1 = skeleton.edges.get(edgeIds[i]);
      const edge2 = skeleton.edges.get(edgeIds[j]);
      if (!edge1 || !edge2) continue;

      // Skip if edges share a node
      if (edge1.startNodeId === edge2.startNodeId || edge1.startNodeId === edge2.endNodeId || edge1.endNodeId === edge2.startNodeId || edge1.endNodeId === edge2.endNodeId) {
        continue;
      }

      const node1Start = skeleton.nodes.get(edge1.startNodeId);
      const node1End = skeleton.nodes.get(edge1.endNodeId);
      const node2Start = skeleton.nodes.get(edge2.startNodeId);
      const node2End = skeleton.nodes.get(edge2.endNodeId);
      if (!node1Start || !node1End || !node2Start || !node2End) continue;

      const intersection = segmentIntersection2D(node1Start.position, node1End.position, node2Start.position, node2End.position);

      if (intersection) {
        crossings.push({
          edge1Id: edgeIds[i],
          edge2Id: edgeIds[j],
          intersection,
        });
      }
    }
  }

  return crossings;
}

/**
 * Resolve edge crossings by reconnecting edges through the closest node
 * without moving any nodes. The closest node to the intersection becomes
 * a junction point where the other edge is split.
 */
function resolveEdgeCrossings(skeleton: FloorSkeleton): void {
  const maxIterations = 100; // Prevent infinite loops

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const crossings = findEdgeCrossings(skeleton);

    if (crossings.length === 0) break;

    // Process one crossing at a time (structure changes after each fix)
    const crossing = crossings[0];
    const edge1 = skeleton.edges.get(crossing.edge1Id);
    const edge2 = skeleton.edges.get(crossing.edge2Id);

    if (!edge1 || !edge2) continue;

    // Get all 4 nodes involved
    const node1Start = skeleton.nodes.get(edge1.startNodeId);
    const node1End = skeleton.nodes.get(edge1.endNodeId);
    const node2Start = skeleton.nodes.get(edge2.startNodeId);
    const node2End = skeleton.nodes.get(edge2.endNodeId);
    if (!node1Start || !node1End || !node2Start || !node2End) continue;

    // Find which node from edge1 is closest to the intersection
    const dist1Start = node1Start.position.distanceTo(crossing.intersection);
    const dist1End = node1End.position.distanceTo(crossing.intersection);
    const closestOnEdge1 = dist1Start < dist1End ? node1Start : node1End;
    const closestDist1 = Math.min(dist1Start, dist1End);

    // Find which node from edge2 is closest to the intersection
    const dist2Start = node2Start.position.distanceTo(crossing.intersection);
    const dist2End = node2End.position.distanceTo(crossing.intersection);
    const closestOnEdge2 = dist2Start < dist2End ? node2Start : node2End;
    const closestDist2 = Math.min(dist2Start, dist2End);

    // The junction node is the one closest to the intersection overall
    // We do NOT move it - just use it as a connection point
    const junctionNode = closestDist1 < closestDist2 ? closestOnEdge1 : closestOnEdge2;
    const edgeContainingJunction = closestDist1 < closestDist2 ? edge1 : edge2;
    const otherEdge = closestDist1 < closestDist2 ? edge2 : edge1;
    const otherEdgeStart = closestDist1 < closestDist2 ? node2Start : node1Start;
    const otherEdgeEnd = closestDist1 < closestDist2 ? node2End : node1End;

    // Reconnect the other edge through the junction node (without moving it)
    // Remove the old edge
    const oldEdgeId = otherEdge.id;
    otherEdgeStart.edgeIds = otherEdgeStart.edgeIds.filter((id) => id !== oldEdgeId);
    otherEdgeEnd.edgeIds = otherEdgeEnd.edgeIds.filter((id) => id !== oldEdgeId);
    skeleton.edges.delete(oldEdgeId);

    // Create two new edges through the junction node
    const timestamp = Date.now();
    const newEdge1Id = `edge_fix_${timestamp}_${iteration}_1`;
    const newEdge2Id = `edge_fix_${timestamp}_${iteration}_2`;

    const newEdge1: SkeletonEdge = {
      id: newEdge1Id,
      startNodeId: otherEdgeStart.id,
      endNodeId: junctionNode.id,
      roomIds: [...otherEdge.roomIds],
      leftSize: otherEdge.leftSize,
      rightSize: otherEdge.rightSize,
      wallType: otherEdge.wallType,
      selected: false,
    };

    const newEdge2: SkeletonEdge = {
      id: newEdge2Id,
      startNodeId: junctionNode.id,
      endNodeId: otherEdgeEnd.id,
      roomIds: [...otherEdge.roomIds],
      leftSize: otherEdge.leftSize,
      rightSize: otherEdge.rightSize,
      wallType: otherEdge.wallType,
      selected: false,
    };

    skeleton.edges.set(newEdge1Id, newEdge1);
    skeleton.edges.set(newEdge2Id, newEdge2);

    // Update node edge references
    otherEdgeStart.edgeIds.push(newEdge1Id);
    junctionNode.edgeIds.push(newEdge1Id);
    junctionNode.edgeIds.push(newEdge2Id);
    otherEdgeEnd.edgeIds.push(newEdge2Id);

    // Merge room IDs from the other edge into the junction node
    for (const roomId of otherEdge.roomIds) {
      if (!junctionNode.roomIds.includes(roomId)) {
        junctionNode.roomIds.push(roomId);
      }
    }

    // Also merge room IDs from the edge containing the junction node
    for (const roomId of edgeContainingJunction.roomIds) {
      if (!junctionNode.roomIds.includes(roomId)) {
        junctionNode.roomIds.push(roomId);
      }
    }
  }
}

/**
 * Snap nodes that are close to edges (T-junctions) onto those edges
 * and split the edges accordingly. This handles cases where a wall from
 * one room meets the middle of a wall from another room.
 *
 * @param skeleton The skeleton to process
 * @param snapTolerance Distance threshold for snapping nodes to edges
 */
function snapNodesToEdges(skeleton: FloorSkeleton, snapTolerance: number): void {
  // Keep track of edges to remove and new edges to add
  const edgesToRemove: string[] = [];
  const edgesToAdd: SkeletonEdge[] = [];
  let newEdgeIdCounter = skeleton.edges.size;

  // For each node, check if it's close to any edge (that it's not already connected to)
  for (const node of skeleton.nodes.values()) {
    let bestEdge: SkeletonEdge | null = null;
    let bestDistance = snapTolerance;
    let bestClosestPoint: THREE.Vector3 | null = null;

    for (const edge of skeleton.edges.values()) {
      // Skip edges that this node is already connected to
      if (edge.startNodeId === node.id || edge.endNodeId === node.id) {
        continue;
      }

      // Skip edges already marked for removal
      if (edgesToRemove.includes(edge.id)) {
        continue;
      }

      const startNode = skeleton.nodes.get(edge.startNodeId);
      const endNode = skeleton.nodes.get(edge.endNodeId);
      if (!startNode || !endNode) continue;

      const { closestPoint, distance, t } = closestPointOnSegment(node.position, startNode.position, endNode.position);

      // Only snap if the point is not too close to the endpoints (avoid snapping to existing nodes)
      // t should be between 0.05 and 0.95 to avoid endpoints
      if (distance < bestDistance && t > 0.05 && t < 0.95) {
        bestEdge = edge;
        bestDistance = distance;
        bestClosestPoint = closestPoint;
      }
    }

    // If we found an edge to snap to, snap the node and split the edge
    if (bestEdge && bestClosestPoint) {
      // Snap the node position to the closest point on the edge
      node.position.copy(bestClosestPoint);

      // Mark the original edge for removal
      edgesToRemove.push(bestEdge.id);

      // Create two new edges: startNode -> snapNode and snapNode -> endNode
      const edge1: SkeletonEdge = {
        id: `edge_snap_${newEdgeIdCounter++}`,
        startNodeId: bestEdge.startNodeId,
        endNodeId: node.id,
        roomIds: [...bestEdge.roomIds],
        leftSize: bestEdge.leftSize,
        rightSize: bestEdge.rightSize,
        wallType: bestEdge.wallType,
        selected: false,
      };

      const edge2: SkeletonEdge = {
        id: `edge_snap_${newEdgeIdCounter++}`,
        startNodeId: node.id,
        endNodeId: bestEdge.endNodeId,
        roomIds: [...bestEdge.roomIds],
        leftSize: bestEdge.leftSize,
        rightSize: bestEdge.rightSize,
        wallType: bestEdge.wallType,
        selected: false,
      };

      edgesToAdd.push(edge1, edge2);

      // Update node edge references for the new edges
      node.edgeIds.push(edge1.id, edge2.id);

      // Merge room IDs from the edge into the node
      for (const roomId of bestEdge.roomIds) {
        if (!node.roomIds.includes(roomId)) {
          node.roomIds.push(roomId);
        }
      }
    }
  }

  // Remove old edges and update node references
  for (const edgeId of edgesToRemove) {
    const edge = skeleton.edges.get(edgeId);
    if (edge) {
      // Remove edge reference from start and end nodes
      const startNode = skeleton.nodes.get(edge.startNodeId);
      const endNode = skeleton.nodes.get(edge.endNodeId);

      if (startNode) {
        startNode.edgeIds = startNode.edgeIds.filter((id) => id !== edgeId);
      }
      if (endNode) {
        endNode.edgeIds = endNode.edgeIds.filter((id) => id !== edgeId);
      }

      skeleton.edges.delete(edgeId);
    }
  }

  // Add new edges and update node references
  for (const edge of edgesToAdd) {
    skeleton.edges.set(edge.id, edge);

    // Add edge reference to nodes (if not already added)
    const startNode = skeleton.nodes.get(edge.startNodeId);
    const endNode = skeleton.nodes.get(edge.endNodeId);

    if (startNode && !startNode.edgeIds.includes(edge.id)) {
      startNode.edgeIds.push(edge.id);
    }
    if (endNode && !endNode.edgeIds.includes(edge.id)) {
      endNode.edgeIds.push(edge.id);
    }
  }
}

/** Configuration for the parallel edge merge pass */
const PARALLEL_EDGE_ANGLE_THRESHOLD = Math.PI / 36; // ~5 degrees

/**
 * Find pairs of nearly parallel edges originating from the same node.
 * Returns the pair with the angle between them and the shorter/longer edge info.
 *
 * @param skeleton The skeleton to search
 * @param angleThreshold Maximum angle (radians) between edges to be considered parallel
 * @returns Array of parallel edge pairs
 */
function findParallelEdgePairs(
  skeleton: FloorSkeleton,
  angleThreshold: number,
): Array<{
  nodeId: string;
  shorterEdgeId: string;
  longerEdgeId: string;
  shorterEndpointId: string;
  longerEndpointId: string;
  angle: number;
}> {
  const result: Array<{
    nodeId: string;
    shorterEdgeId: string;
    longerEdgeId: string;
    shorterEndpointId: string;
    longerEndpointId: string;
    angle: number;
  }> = [];

  for (const [nodeId, node] of skeleton.nodes) {
    // Only consider nodes with at least 2 edges
    if (node.edgeIds.length < 2) continue;

    // Collect edge directions and lengths from this node
    const edgeData: Array<{
      edgeId: string;
      direction: THREE.Vector2;
      length: number;
      endpointId: string;
    }> = [];

    for (const edgeId of node.edgeIds) {
      const edge = skeleton.edges.get(edgeId);
      if (!edge) continue;

      const endpointId = edge.startNodeId === nodeId ? edge.endNodeId : edge.startNodeId;
      const endpoint = skeleton.nodes.get(endpointId);
      if (!endpoint) continue;

      const dx = endpoint.position.x - node.position.x;
      const dy = endpoint.position.y - node.position.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length < 0.001) continue; // Skip degenerate edges

      const direction = new THREE.Vector2(dx / length, dy / length);
      edgeData.push({ edgeId, direction, length, endpointId });
    }

    // Compare all pairs of edges from this node
    for (let i = 0; i < edgeData.length; i++) {
      for (let j = i + 1; j < edgeData.length; j++) {
        const edge1 = edgeData[i];
        const edge2 = edgeData[j];

        // Calculate angle between the two edge directions
        // dot(a, b) = |a||b|cos(angle), but directions are normalized
        const dot = edge1.direction.x * edge2.direction.x + edge1.direction.y * edge2.direction.y;
        // Clamp to avoid floating point issues with acos
        const clampedDot = Math.max(-1, Math.min(1, dot));
        const angle = Math.acos(clampedDot);

        // Check if edges are nearly parallel (small angle between them)
        if (angle < angleThreshold) {
          // Determine which edge is shorter/longer
          const [shorter, longer] = edge1.length <= edge2.length ? [edge1, edge2] : [edge2, edge1];

          result.push({
            nodeId,
            shorterEdgeId: shorter.edgeId,
            longerEdgeId: longer.edgeId,
            shorterEndpointId: shorter.endpointId,
            longerEndpointId: longer.endpointId,
            angle,
          });
        }
      }
    }
  }

  // Sort by angle (smallest first) to process the most parallel edges first
  result.sort((a, b) => a.angle - b.angle);

  return result;
}

/**
 * Merge nearly parallel edges originating from the same node.
 * The longer edge is reconnected to the endpoint of the shorter edge,
 * and the shorter edge is removed.
 *
 * @param skeleton The skeleton to process
 * @param angleThreshold Maximum angle (radians) between edges to be considered parallel
 */
function mergeParallelEdges(skeleton: FloorSkeleton, angleThreshold: number = PARALLEL_EDGE_ANGLE_THRESHOLD): void {
  const maxIterations = 100;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const parallelPairs = findParallelEdgePairs(skeleton, angleThreshold);

    if (parallelPairs.length === 0) break;

    // Process one pair at a time (structure changes after each merge)
    const pair = parallelPairs[0];

    const originNode = skeleton.nodes.get(pair.nodeId);
    const shorterEdge = skeleton.edges.get(pair.shorterEdgeId);
    const longerEdge = skeleton.edges.get(pair.longerEdgeId);
    const shorterEndpoint = skeleton.nodes.get(pair.shorterEndpointId);
    const longerEndpoint = skeleton.nodes.get(pair.longerEndpointId);

    if (!originNode || !shorterEdge || !longerEdge || !shorterEndpoint || !longerEndpoint) continue;

    // Reconnect the longer edge to the shorter edge's endpoint
    // Update the longer edge's endpoint that was at the origin node
    if (longerEdge.startNodeId === pair.nodeId) {
      longerEdge.startNodeId = pair.shorterEndpointId;
    } else {
      longerEdge.endNodeId = pair.shorterEndpointId;
    }

    // Update node edge references
    // Remove longer edge from origin node
    originNode.edgeIds = originNode.edgeIds.filter((id) => id !== pair.longerEdgeId);

    // Add longer edge to shorter endpoint (if not already connected)
    if (!shorterEndpoint.edgeIds.includes(pair.longerEdgeId)) {
      shorterEndpoint.edgeIds.push(pair.longerEdgeId);
    }

    // Merge room IDs from longer edge into shorter endpoint
    for (const roomId of longerEdge.roomIds) {
      if (!shorterEndpoint.roomIds.includes(roomId)) {
        shorterEndpoint.roomIds.push(roomId);
      }
    }

    // Check if longer edge became a self-loop (both endpoints are the same)
    if (longerEdge.startNodeId === longerEdge.endNodeId) {
      // Remove the self-loop
      skeleton.edges.delete(pair.longerEdgeId);
      shorterEndpoint.edgeIds = shorterEndpoint.edgeIds.filter((id) => id !== pair.longerEdgeId);
      longerEndpoint.edgeIds = longerEndpoint.edgeIds.filter((id) => id !== pair.longerEdgeId);
    }

    // Check for duplicate edges between shorter endpoint and longer endpoint
    // (There might already be an edge between them)
    const existingEdge = findEdgeBetweenNodes(skeleton, pair.shorterEndpointId, pair.longerEndpointId);
    if (existingEdge && existingEdge.id !== pair.longerEdgeId) {
      // Merge room IDs from longer edge into existing edge
      for (const roomId of longerEdge.roomIds) {
        if (!existingEdge.roomIds.includes(roomId)) {
          existingEdge.roomIds.push(roomId);
        }
      }
      // Remove the duplicate longer edge
      skeleton.edges.delete(pair.longerEdgeId);
      shorterEndpoint.edgeIds = shorterEndpoint.edgeIds.filter((id) => id !== pair.longerEdgeId);
      longerEndpoint.edgeIds = longerEndpoint.edgeIds.filter((id) => id !== pair.longerEdgeId);
    }
  }
}

/**
 * Merge nodes with distinct origins (different roomIds) when there are no
 * conflicting origins nearby. This handles cases where two nodes from different
 * rooms should logically be the same node but weren't merged during initial
 * creation because they came from different rooms.
 *
 * A merge is only performed when:
 * 1. Two nodes are within mergeTolerance distance
 * 2. They have completely distinct roomIds (no overlap)
 * 3. No other nodes in the vicinity share any of the same roomIds (no conflicts)
 *
 * @param skeleton The skeleton to process
 * @param mergeTolerance Distance threshold for considering nodes as merge candidates
 */
function mergeNodesWithDistinctOrigins(skeleton: FloorSkeleton, mergeTolerance: number): void {
  const maxIterations = 100;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    let merged = false;

    // Find candidate pairs: nodes within tolerance that have distinct origins
    const nodeIds = Array.from(skeleton.nodes.keys());

    for (let i = 0; i < nodeIds.length && !merged; i++) {
      for (let j = i + 1; j < nodeIds.length && !merged; j++) {
        const nodeA = skeleton.nodes.get(nodeIds[i]);
        const nodeB = skeleton.nodes.get(nodeIds[j]);

        if (!nodeA || !nodeB) continue;

        // Check distance
        const distance = nodeA.position.distanceTo(nodeB.position);
        if (distance > mergeTolerance) continue;

        // Check for distinct origins (no overlap in roomIds)
        const hasOverlap = !nodeA.roomIds.some((roomId) => nodeB.roomIds.includes(roomId));
        if (hasOverlap) continue;

        // Collect all roomIds from both nodes
        const combinedRoomIds = new Set([...nodeA.roomIds, ...nodeB.roomIds]);

        // Check for conflicting origins in nearby nodes
        // A conflict exists if another node (not A or B) is close and shares
        // any roomId with either A or B
        let hasConflict = false;

        for (const [otherId, otherNode] of skeleton.nodes) {
          if (otherId === nodeA.id || otherId === nodeB.id) continue;

          // Check if this other node is close to the merge location
          const midpoint = new THREE.Vector3().addVectors(nodeA.position, nodeB.position).multiplyScalar(0.5);
          const otherDistance = otherNode.position.distanceTo(midpoint);

          // Use a larger search radius to detect conflicts
          if (otherDistance > mergeTolerance * 3) continue;

          // Check if the other node shares any roomId with our candidates
          const sharesRoomId = otherNode.roomIds.some((roomId) => combinedRoomIds.has(roomId));
          if (sharesRoomId) {
            hasConflict = true;
            break;
          }
        }

        if (hasConflict) continue;

        // Safe to merge: no overlapping origins and no conflicts nearby
        // Merge B into A
        const midpoint = new THREE.Vector3().addVectors(nodeA.position, nodeB.position).multiplyScalar(0.5);
        nodeA.position.copy(midpoint);

        // Merge room IDs
        for (const roomId of nodeB.roomIds) {
          if (!nodeA.roomIds.includes(roomId)) {
            nodeA.roomIds.push(roomId);
          }
        }

        // Merge source-vertex contributions
        for (const sv of nodeB.sourceVertices) {
          nodeA.sourceVertices.push(sv);
        }

        // Redirect all edges from nodeB to nodeA
        for (const edgeId of nodeB.edgeIds) {
          const edge = skeleton.edges.get(edgeId);
          if (!edge) continue;

          // Update edge endpoint
          if (edge.startNodeId === nodeB.id) {
            edge.startNodeId = nodeA.id;
          }
          if (edge.endNodeId === nodeB.id) {
            edge.endNodeId = nodeA.id;
          }

          // Add edge to nodeA if not already there
          if (!nodeA.edgeIds.includes(edgeId)) {
            nodeA.edgeIds.push(edgeId);
          }

          // Remove self-loops
          if (edge.startNodeId === edge.endNodeId) {
            skeleton.edges.delete(edgeId);
            nodeA.edgeIds = nodeA.edgeIds.filter((id) => id !== edgeId);
          }
        }

        // Remove duplicate edges (same start and end nodes)
        const edgeSignatures = new Map<string, string>();
        const duplicateEdges: string[] = [];

        for (const edgeId of nodeA.edgeIds) {
          const edge = skeleton.edges.get(edgeId);
          if (!edge) continue;

          const sig = [edge.startNodeId, edge.endNodeId].sort().join('-');
          if (edgeSignatures.has(sig)) {
            // Merge room IDs into existing edge
            const existingEdgeId = edgeSignatures.get(sig);
            const existingEdge = existingEdgeId ? skeleton.edges.get(existingEdgeId) : undefined;
            if (!existingEdge) continue;
            for (const roomId of edge.roomIds) {
              if (!existingEdge.roomIds.includes(roomId)) {
                existingEdge.roomIds.push(roomId);
              }
            }
            duplicateEdges.push(edgeId);
          } else {
            edgeSignatures.set(sig, edgeId);
          }
        }

        // Remove duplicate edges
        for (const edgeId of duplicateEdges) {
          const edge = skeleton.edges.get(edgeId);
          if (edge) {
            const startNode = skeleton.nodes.get(edge.startNodeId);
            const endNode = skeleton.nodes.get(edge.endNodeId);
            if (startNode) {
              startNode.edgeIds = startNode.edgeIds.filter((id) => id !== edgeId);
            }
            if (endNode) {
              endNode.edgeIds = endNode.edgeIds.filter((id) => id !== edgeId);
            }
            skeleton.edges.delete(edgeId);
          }
        }

        // Remove nodeB
        skeleton.nodes.delete(nodeB.id);

        merged = true;
      }
    }

    if (!merged) break;
  }
}

/**
 * Generate a skeleton by expanding room polygons outward using ray casting.
 *
 * Algorithm:
 * 1. For each vertex, cast rays outward to find nearby walls from other rooms
 * 2. If a ray hits, offset by half the distance (wall centerline)
 * 3. For concave vertices, use inner wall thickness / 2
 * 4. For vertices with no hits (outer walls), use outer wall thickness / 2
 * 5. Merge nearby expanded points to form skeleton nodes
 * 6. Create edges based on original room connectivity
 * 7. Collapse small triangles where all edges are below threshold
 *
 * @param rooms Array of room data with identifier and polygon
 * @param floorHeight The floor height for the skeleton
 * @param config Configuration for wall thicknesses and tolerances
 * @returns The generated skeleton and debug data
 */
export function generateSkeletonFromRooms(
  rooms: Array<{ identifier: string; polygon: THREE.Vector3[] }>,
  floorHeight: number,
  config: Partial<FloorExpansionConfig> = {},
  precomputedWallRegion?: WallRegionComponent[],
): GenerateSkeletonResult {
  const fullConfig: FloorExpansionConfig = { ...DEFAULT_EXPANSION_CONFIG, ...config };

  const skeleton: FloorSkeleton = {
    nodes: new Map(),
    edges: new Map(),
    floorHeight,
  };

  const debugData: SkeletonDebugData = {
    vertices: [],
    wallRegion: [],
    roomPolygonsById: new Map<string, THREE.Vector3[]>(),
  };

  if (rooms.length === 0) {
    return { skeleton, debugData };
  }

  // Normalize all real-room polygons to CW. The expansion math assumes CW
  // (perpendicular `(-dy, dx)` points away from the room interior into the
  // wall material). A CCW room would invert the bisector and inset the
  // skeleton into the room — and would also confuse the wall-region union.
  rooms = rooms.map((r) => {
    const poly = r.polygon;
    if (poly.length < 3) return r;
    let signedArea = 0;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      signedArea += (b.x - a.x) * (b.y + a.y);
    }
    // CW has positive signed area in this formulation; CCW negative.
    return signedArea >= 0 ? r : { ...r, polygon: r.polygon.slice().reverse() };
  });

  // Synthesize pseudo-rooms from the inflated boolean union of the real rooms.
  // Outer ring stays CCW (as it comes from polygon-clipping); its vertices are
  // flagged so expandVertex inverts the bisector / ray fan to point INTO the
  // wall material (toward the building) instead of outward into empty space.
  // Cavity rings come back CW around the cavity void; the existing CW-based
  // math already points their bisectors into the wall region — no flip needed.
  // Caller may supply a precomputed wall region (e.g. the floor's
  // existing outline) so we don't synthesize a new envelope from the
  // rooms. The downstream synthetic-room normalization re-asserts
  // outer-CCW / cavity-CW windings, so callers don't need to.
  const wallRegion = precomputedWallRegion ?? buildWallRegionFromRooms(rooms, fullConfig.outerWallThickness);
  debugData.wallRegion = wallRegion;

  const syntheticRooms: Array<{ identifier: string; polygon: THREE.Vector3[] }> = [];
  for (let c = 0; c < wallRegion.length; c++) {
    const comp = wallRegion[c];
    syntheticRooms.push({
      identifier: `${OUTER_ROOM_ID_PREFIX}${c}`,
      polygon: comp.outerRing.map((p) => new THREE.Vector3(p.x, p.y, floorHeight)),
    });
    for (let h = 0; h < comp.holes.length; h++) {
      syntheticRooms.push({
        identifier: `${CAVITY_ROOM_ID_PREFIX}${c}_${h}`,
        polygon: comp.holes[h].map((p) => new THREE.Vector3(p.x, p.y, floorHeight)),
      });
    }
  }

  // Normalize synthetic-room windings to the convention downstream
  // relies on:
  //  - outer shells: CCW (interior = outdoor region around the
  //    building). `expandVertex` flips its probe fan via
  //    `invertOutward` so the rays still shoot INTO the wall material
  //    despite the CCW winding. `castSingleRay` inverts its side check
  //    for these walls so the ray-origin-on-wall-material rule reads
  //    as "origin INSIDE the CCW polygon" (= the outdoor region).
  //  - cavities: CW around the void (interior = the cavity void).
  //    Same expand / side semantics as real rooms — rays approach
  //    from outside the polygon.
  // `WallRegionBuilder` already promises these orientations, but a
  // degenerate ring can occasionally come back inverted; normalize
  // here so the downstream invariants hold unconditionally.
  for (const sr of syntheticRooms) {
    const poly = sr.polygon;
    if (poly.length < 3) continue;
    // Signed area: same shoelace alternate used for real rooms above
    // (`Σ (b.x - a.x) * (b.y + a.y)`). Positive → CW, negative → CCW.
    let signedArea = 0;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      signedArea += (b.x - a.x) * (b.y + a.y);
    }
    const isOuter = sr.identifier.startsWith(OUTER_ROOM_ID_PREFIX);
    const wantCW = !isOuter; // cavities CW, outer shells CCW
    const isCW = signedArea >= 0;
    if (isCW !== wantCW) sr.polygon = poly.slice().reverse();
  }

  // Process synthetic boundaries alongside the real rooms so they contribute
  // walls (ray targets), vertices (skeleton nodes), and edges (skeleton edges).
  const allRooms = [...rooms, ...syntheticRooms];

  for (const room of allRooms) {
    debugData.roomPolygonsById.set(room.identifier, room.polygon);
  }

  // Collect all walls in 2D for ray casting, with indices
  // Also track which wall indices are adjacent to each vertex
  const allWalls2D: WallWithIndex[] = [];
  // Maps vertex global index -> [prevEdgeWallIndex, nextEdgeWallIndex]
  const vertexAdjacentWalls: Map<number, [number, number]> = new Map();

  let wallIndex = 0;
  let globalVertexIndex = 0;

  for (const room of allRooms) {
    const polygon = room.polygon;
    const roomWallStartIndex = wallIndex;

    // `materialSideNormal = (-dy, dx)` (left-of-travel) is the
    // wall-material side under every polygon kind we feed in:
    //  - CW rooms / cavities → left-of-travel is OUT of the polygon
    //    interior = outside the room / outside the cavity void.
    //  - CCW outer shells    → left-of-travel is INTO the polygon
    //    interior = the outdoor region surrounding the building,
    //    which is the wall-material side per the user's framing.
    // The side filter in `castSingleRay` requires the ray origin to
    // sit on this side; the same `offset > 0` test serves all kinds.
    for (let i = 0; i < polygon.length; i++) {
      const start = polygon[i];
      const end = polygon[(i + 1) % polygon.length];
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const len = Math.hypot(dx, dy);
      const nx = len > 1e-9 ? -dy / len : 0;
      const ny = len > 1e-9 ? dx / len : 0;
      allWalls2D.push({
        start: new THREE.Vector2(start.x, start.y),
        end: new THREE.Vector2(end.x, end.y),
        roomId: room.identifier,
        index: wallIndex,
        edgeIndexInRoom: i,
        materialSideNormal: new THREE.Vector2(nx, ny),
      });
      wallIndex++;
    }

    // Second pass: map vertices to their adjacent wall indices
    // For vertex i in a room:
    //   - prevEdge is the wall from vertex (i-1) to vertex i, which is wall index (i-1) in the room
    //   - nextEdge is the wall from vertex i to vertex (i+1), which is wall index i in the room
    for (let i = 0; i < polygon.length; i++) {
      const prevEdgeIndex = roomWallStartIndex + ((i - 1 + polygon.length) % polygon.length);
      const nextEdgeIndex = roomWallStartIndex + i;
      vertexAdjacentWalls.set(globalVertexIndex, [prevEdgeIndex, nextEdgeIndex]);
      globalVertexIndex++;
    }
  }

  // Build vertex input data with edge directions
  const allVertices: VertexInput[] = [];
  const roomsWithStartIndex: Array<{ identifier: string; polygon: THREE.Vector3[]; vertexStartIndex: number }> = [];

  let vertexIndex = 0;
  for (const room of allRooms) {
    const polygon = room.polygon;
    const startIndex = vertexIndex;
    const isOuterShell = room.identifier.startsWith(OUTER_ROOM_ID_PREFIX);

    for (let i = 0; i < polygon.length; i++) {
      const prev = polygon[(i - 1 + polygon.length) % polygon.length];
      const curr = polygon[i];
      const next = polygon[(i + 1) % polygon.length];

      // Edge lengths (used by the probe-based expansion to walk inward along each edge)
      const prevEdgeLength = Math.hypot(curr.x - prev.x, curr.y - prev.y);
      const nextEdgeLength = Math.hypot(next.x - curr.x, next.y - curr.y);

      // Direction from previous vertex to current (incoming edge)
      const prevDir = new THREE.Vector2(curr.x - prev.x, curr.y - prev.y).normalize();

      // Direction from current to next vertex (outgoing edge)
      const nextDir = new THREE.Vector2(next.x - curr.x, next.y - curr.y).normalize();

      // Project to the canonical floor height so all expanded vertices share
      // the same Z. Without this, real-room corners (which keep their
      // original Z, potentially ±0.5m off the floor plane) and synthetic
      // outer/cavity corners (placed at floorHeight) end up at different Zs
      // and `distanceTo` (3D) blocks the close-node merge pass.
      allVertices.push({
        position: new THREE.Vector3(curr.x, curr.y, floorHeight),
        roomId: room.identifier,
        prevDirection: prevDir,
        nextDirection: nextDir,
        prevEdgeLength,
        nextEdgeLength,
        invertOutward: isOuterShell,
      });

      vertexIndex++;
    }

    roomsWithStartIndex.push({
      identifier: room.identifier,
      polygon: room.polygon,
      vertexStartIndex: startIndex,
    });
  }

  // Expand each vertex outward
  const expandedVerticesRaw: Array<{ position: THREE.Vector3; roomId: string; originalIndex: number; vertexIndexInRoom: number; probedHits: SkeletonProbedHit[] }> = [];

  // Map global vertex index -> local index within its room's polygon.
  const vertexIndexInRoomByGlobal = new Map<number, number>();
  {
    let g = 0;
    for (const room of allRooms) {
      for (let k = 0; k < room.polygon.length; k++) {
        vertexIndexInRoomByGlobal.set(g, k);
        g++;
      }
    }
  }

  // Bisector-near-hit snap: if a vertex's bisector ray nearly hits another
  // original room vertex, drop the regular ray/wall offset and place the
  // expanded position at the midpoint between the original vertex and the
  // found target. Generated points (skeleton nodes, already-expanded
  // vertices) are deliberately excluded so the rule is symmetric — both
  // sides of a near-hit pair see the same originals and converge to the
  // same midpoint.
  //
  // Alignment is gated by ANGLE rather than perpendicular distance, so close
  // candidates have a wide tolerance and far candidates a tight one — which
  // matches what "the bisector points at this vertex" actually means.
  const nearHitMaxReach = fullConfig.maxRayDistance;
  const nearHitMaxAngle = (1 * Math.PI) / 180; // ±1°
  const nearHitMinCos = Math.cos(nearHitMaxAngle);

  for (let i = 0; i < allVertices.length; i++) {
    const vertex = allVertices[i];
    // Get the wall indices to skip (the two edges adjacent to this vertex)
    const adjacentWalls = vertexAdjacentWalls.get(i);
    const skipWallIndices = adjacentWalls ? new Set<number>([adjacentWalls[0], adjacentWalls[1]]) : new Set<number>();

    const result = expandVertex(vertex, allWalls2D, skipWallIndices, fullConfig, i);

    // Bisector starts at the original vertex and points along result.debugData.bisectorDirection.
    const origin2D = new THREE.Vector2(vertex.position.x, vertex.position.y);
    const bisector2D = new THREE.Vector2(result.debugData.bisectorDirection.x, result.debugData.bisectorDirection.y);
    const bisectorLen = bisector2D.length();
    let snappedPosition: THREE.Vector3 | null = null;
    if (bisectorLen > 1e-6) {
      bisector2D.divideScalar(bisectorLen);

      let bestForward = Infinity;
      let bestTargetXY: THREE.Vector2 | null = null;

      const considerCandidate = (cx: number, cy: number): void => {
        const dx = cx - origin2D.x;
        const dy = cy - origin2D.y;
        const dist = Math.hypot(dx, dy);
        if (dist <= 1e-6 || dist > nearHitMaxReach) return;
        // cos(angle) between bisector and origin->candidate direction.
        const cosAngle = (dx * bisector2D.x + dy * bisector2D.y) / dist;
        if (cosAngle < nearHitMinCos) return;
        const forward = dx * bisector2D.x + dy * bisector2D.y;
        if (forward < bestForward) {
          bestForward = forward;
          bestTargetXY = new THREE.Vector2(cx, cy);
        }
      };

      // Only original room vertices — generated points are excluded so the
      // rule is symmetric across both sides of a near-hit pair.
      for (let j = 0; j < allVertices.length; j++) {
        if (j === i) continue;
        const other = allVertices[j].position;
        considerCandidate(other.x, other.y);
      }

      if (bestTargetXY) {
        const target = bestTargetXY as THREE.Vector2;
        snappedPosition = new THREE.Vector3((origin2D.x + target.x) / 2, (origin2D.y + target.y) / 2, vertex.position.z);
      }
    }

    const finalPosition = snappedPosition ?? result.position;
    if (snappedPosition) {
      result.debugData.expandedPosition = finalPosition.clone();
    }

    // Convert per-vertex probe hits (which only know the source-edge kind +
    // hit polygon edge) into SkeletonProbedHit by filling in this vertex's
    // own source room/index. These ride along on the node so a later pass
    // can ask "which polygon edges drove this node's position?".
    const sourceRoomId = vertex.roomId;
    const sourceVertexIndex = vertexIndexInRoomByGlobal.get(i) ?? 0;
    const skeletonProbedHits: SkeletonProbedHit[] = result.probedHits.map((h) => ({
      sourceRoomId,
      sourceVertexIndex,
      sourceEdgeKind: h.sourceEdgeKind,
      hitRoomId: h.hitRoomId,
      hitEdgeIndex: h.hitEdgeIndex,
    }));

    expandedVerticesRaw.push({
      position: finalPosition,
      roomId: vertex.roomId,
      originalIndex: i,
      vertexIndexInRoom: sourceVertexIndex,
      probedHits: skeletonProbedHits,
    });
    debugData.vertices.push(result.debugData);
  }

  // When using external pipeline, populate skeleton with data for pipeline passes
  if (fullConfig.skipCleanupPasses) {
    // Convert to ExpandedVertex format for pipeline
    skeleton.expandedVertices = expandedVerticesRaw.map(
      (v) =>
        ({
          position: v.position.clone(),
          roomId: v.roomId,
          originalIndex: v.originalIndex,
          vertexIndexInRoom: v.vertexIndexInRoom,
          probedHits: v.probedHits.slice(),
        }) as ExpandedVertex,
    );

    // Store room connectivity for edge creation pass
    skeleton.roomConnectivity = roomsWithStartIndex.map(
      (r) =>
        ({
          identifier: r.identifier,
          polygon: r.polygon,
          vertexStartIndex: r.vertexStartIndex,
        }) as RoomConnectivity,
    );

    // Pipeline will handle node creation, merging, and edge creation
    return { skeleton, debugData };
  }

  // Legacy path: create nodes and edges directly (when not using pipeline)
  const vertexToNodeMap = createNodesFromExpandedVertices(skeleton, expandedVerticesRaw, fullConfig.mergeTolerance);
  createEdgesFromConnectivity(skeleton, roomsWithStartIndex, vertexToNodeMap);

  // Handle T-junctions: snap nodes close to edges and split those edges
  // Use a slightly larger tolerance than merge tolerance to catch T-junctions
  const snapTolerance = fullConfig.mergeTolerance * 2;
  snapNodesToEdges(skeleton, snapTolerance);

  // Clean up any remaining edge crossings
  resolveEdgeCrossings(skeleton);

  // Merge nearly parallel edges from the same node
  // This handles cases where two edges go in almost the same direction
  mergeParallelEdges(skeleton);

  // Merge nodes with distinct origins when no conflicting origins are nearby
  // This handles cases where nodes from different rooms should be merged
  mergeNodesWithDistinctOrigins(skeleton, fullConfig.mergeTolerance);

  return { skeleton, debugData };
}
